"use client";

import AuthorizedDistributorBadge from "@/components/authorized-distributor-badge/authorizedDistributorBadge";
import VendorPicker from "@/app/ec/product/[basePath]/vendor-options/vendorPicker";
import CaretDownIcon from "public/next-public/common/icons/caret-solid-down.svg";
import { useCallback, useEffect, useRef, useState } from "react";
import styles from "./vendorSelector.module.scss";

interface VendorSelectorProps {
  vendorOptions: VendorOptionState[];
  chosenVendorKey: number;
  setChosenVendorKey: (index: number) => void;
  quantity: number;
  getVendorOptionsCallback?: () => void;
  vendorOptionsAreNotFetched?: boolean;
}

export default function VendorSelector({
  vendorOptions,
  chosenVendorKey,
  setChosenVendorKey,
  quantity,
  getVendorOptionsCallback,
  vendorOptionsAreNotFetched,
}: VendorSelectorProps) {
  const [isPopoverVisible, setIsPopoverVisible] = useState(false);
  const targetRef = useRef(null);

  const selectedVendor = vendorOptions[chosenVendorKey];

  const showDropdown = Boolean(getVendorOptionsCallback && vendorOptionsAreNotFetched) || vendorOptions.length > 1;

  useEffect(() => {
    if (!showDropdown) {
      setIsPopoverVisible(false);
    }
  }, [showDropdown]);

  const handleClick = useCallback(() => {
    if (vendorOptionsAreNotFetched && getVendorOptionsCallback) {
      getVendorOptionsCallback();
    }
    setIsPopoverVisible(true);
  }, [vendorOptionsAreNotFetched, getVendorOptionsCallback]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleClick();
      }
    },
    [handleClick],
  );

  const handleHidePopover = useCallback(() => {
    setIsPopoverVisible(false);
  }, []);

  if (!selectedVendor) return null;

  return (
    <div className={styles.VendorSelector}>
      <span className={styles.soldByWrapper}>
        <span className={styles.soldByLabel}>Sold by </span>
        <span
          ref={targetRef}
          className={`${styles.vendorName} ${showDropdown ? styles.hasDropdown : ""}`}
          onClick={showDropdown ? handleClick : undefined}
          onKeyDown={showDropdown ? handleKeyDown : undefined}
          tabIndex={showDropdown ? 0 : undefined}
          role={showDropdown ? "button" : undefined}
          aria-expanded={showDropdown ? isPopoverVisible : undefined}
        >
          {selectedVendor.vendorName}
          {showDropdown && <CaretDownIcon className={styles.caretIcon} width={12} height={12} aria-hidden />}
        </span>
      </span>

      {selectedVendor.isAuthorizedDistributor && <AuthorizedDistributorBadge />}

      {showDropdown && (
        <VendorPicker
          vendorOptions={vendorOptions}
          quantity={quantity}
          chosenVendorKey={chosenVendorKey}
          visible={isPopoverVisible}
          hideCallback={handleHidePopover}
          targetRef={targetRef}
          setChosenVendorKey={setChosenVendorKey}
          vendorOptionsAreNotFetched={vendorOptionsAreNotFetched}
        />
      )}
    </div>
  );
}
